import React, { useEffect, useState } from 'react';
import { Receipt, Delete } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import { ITableInvoice } from '../../../main/interfaces/ITableInvocie';
import IProduct from '../../../main/interfaces/IProduct';
import IInvoiceOrder from '../../../main/interfaces/IInvoiceOrder';
import ISubject from '../../../main/interfaces/ISubject';
import useGetUser from '../../../main/hooks/useGetUser';
import GeneralModal from '../../../main/components/modal/general/GeneralModal';
import './Invoice.css';

interface InvoiceSectionProps {
  order?: { [key: number]: { product: IProduct; quantity: number } };
  neworder: IInvoiceOrder[];
  totalPrice: number;
  invoice?: ITableInvoice;
  updateOrder?: (item: any, product: any, quantity: any, isEditingOnInvoice: boolean) => void;
  removeOrder?: (item: IInvoiceOrder) => void;
  handleCloseInvoice?: () => void;
  handleCloseTemp?: () => void;
  isInvoiceActionInFlight?: boolean;
  canManageTableActions?: boolean;
  closeAction?: () => void;
  onScrollTop?: () => void;
  subjects?: ISubject[];
  selectedSubjectId?: string;
  onSubjectChange?: (subjectId: string) => void;
  onEditNote?: (order: IInvoiceOrder) => void;
  tableNote?: string;
  onEditTableNote?: () => void;
}

const InvoiceSection: React.FC<InvoiceSectionProps> = ({
  neworder,
  totalPrice,
  invoice,
  updateOrder,
  removeOrder,
  handleCloseInvoice,
  handleCloseTemp,
  isInvoiceActionInFlight = false,
  canManageTableActions = true,
  onScrollTop,
  subjects = [],
  selectedSubjectId = '',
  onSubjectChange,
  onEditNote,
  tableNote = '',
  onEditTableNote,
}) => {
  const [showOldInvoice, setShowOldInvoice] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [editableDrafts, setEditableDrafts] = useState<Record<number, string>>({});

  const { i18n } = useTranslation();
  const user = useGetUser();

  useEffect(() => {
    setShowOldInvoice(false);
    setEditableDrafts({});
  }, [invoice?.id]);

  const toggleOldInvoice = () => {
    setShowOldInvoice((prev) => !prev);
  };

  const handleCloseClick = () => {
    setShowCloseConfirm(true);
  };

  const confirmCloseInvoice = () => {
    if (!handleCloseInvoice) return;
    setShowCloseConfirm(false);
    handleCloseInvoice();
  };

  const getRowKey = (row: IInvoiceOrder) => Number(row.index ?? row.product?.id ?? 0);
  const getOldInvoiceUnitPrice = (row: NonNullable<ITableInvoice['invoiceRows']>[number]) =>
    Number(row.product?.price ?? 0);
  const getOldInvoiceLineTotal = (row: NonNullable<ITableInvoice['invoiceRows']>[number]) =>
    getOldInvoiceUnitPrice(row) * Number(row.quantity ?? 0);

  const oldInvoiceTotal = Array.isArray(invoice?.invoiceRows)
    ? invoice.invoiceRows.reduce((acc, row) => acc + getOldInvoiceLineTotal(row), 0)
    : 0;

  return (
    <div className="oiInv__section">
      <div className="oiInv__card oiInv__cardCurrent">
        <div className="oiInv__header oiInv__headerCurrent">
          <div className="oiInv__headerActions">
            <button type="button" onClick={onScrollTop} className="oiInv__actionBtn oiInv__actionBtnElevator">
              &uarr;
            </button>
            <button
              type="button"
              onClick={onEditTableNote}
              className={`oiInv__actionBtn oiInv__actionBtnTableNote ${tableNote ? 'has-note' : ''}`}
              title={tableNote || 'Shto shenim per tavolinen'}
              aria-label="Shenim per tavolinen"
              disabled={!canManageTableActions}
            >
              <EditIcon style={{ fontSize: '16px' }} />
            </button>
            <button type="button" onClick={toggleOldInvoice} className="oiInv__actionBtn oiInv__actionBtnHistory">
              {i18n.t('tables.history')}
            </button>
            {canManageTableActions && (
              <button type="button" className="oiInv__actionBtn oiInv__actionBtnCounter" onClick={handleCloseTemp} disabled={isInvoiceActionInFlight}>
                {i18n.t('tables.counter')}
              </button>
            )}
            {canManageTableActions && (
              <button type="button" className="oiInv__actionBtn oiInv__actionBtnClose" onClick={handleCloseClick} disabled={isInvoiceActionInFlight}>
                {i18n.t('tables.close')}
              </button>
            )}
          </div>
          <span className="oiInv__items-count">
            {neworder.length}
          </span>
        </div>

        {neworder.length === 0 ? (
          <div className="empty-order">
            <div className="empty-icon">&#128722;</div>
            <p>{i18n.t('tables.noItemsInInvoice')}</p>
          </div>
        ) : (
          <div className="oiInv__list oiInv__listCurrent">
            {neworder.map((row, index) => (
              <div key={index} className="oiInv__row">
                <div className="oiInv__left">
                  <div className="oiInv__meta">
                    <div className="oiInv__sub">
                      <div className="oiInv__name-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span className="oiInv__name">{row.product?.name}</span>

                        <button
                          type="button"
                          className={`oiInv__noteBtn ${row.note ? 'has-note' : ''}`}
                          onClick={() => onEditNote?.(row)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            color: row.note ? '#2e7d32' : '#757575',
                          }}
                          title={row.note || 'Shto shenim'}
                        >
                          <EditIcon style={{ fontSize: '16px' }} />
                        </button>
                      </div>

                      {/* <span className="oiInv__linePrice" style={{ fontSize: '13px', color: '#666' }}>
                        {row.product?.price.toFixed(2)} {user?.companyCurrencySymbol ?? ''}
                      </span> */}

                      {row.note && (
                        <div className="oiInv__note-display" style={{ fontSize: '11px', color: '#2e7d32', fontStyle: 'italic', marginTop: '2px' }}>
                          {row.note}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="oiInv__right">
                  <div className="oiInv__actions">
                    {row.product?.isEditable ? (
                      <input
                        className="oiInv__qtyInput"
                        name="quantity"
                        value={
                          Object.prototype.hasOwnProperty.call(editableDrafts, getRowKey(row))
                            ? editableDrafts[getRowKey(row)]
                            : row.quantity === 0
                              ? ''
                              : String(row.quantity)
                        }
                        type="number"
                        min={0}
                        step="any"
                        onChange={(e) => {
                          const nextValue = e.target.value;
                          const rowKey = getRowKey(row);
                          setEditableDrafts((prev) => ({ ...prev, [rowKey]: nextValue }));
                          updateOrder?.(row, row.product, nextValue === '' ? 0 : parseFloat(nextValue), true);
                        }}
                        onBlur={() => {
                          const rowKey = getRowKey(row);
                          setEditableDrafts((prev) => {
                            const next = { ...prev };
                            delete next[rowKey];
                            return next;
                          });
                        }}
                      />
                    ) : (
                      <>
                        <button type="button" className="oiInv__btn" onClick={() => updateOrder?.(row, row.product, row.quantity - 1, true)}>
                          -
                        </button>
                        <span className="oiInv__qty">{row.quantity}</span>
                        <button type="button" className="oiInv__btn" onClick={() => updateOrder?.(row, row.product, row.quantity + 1, true)}>
                          +
                        </button>
                      </>
                    )}

                    <button type="button" className="oiInv__deleteBtn" onClick={() => removeOrder?.(row)} title="Delete product">
                      <Delete fontSize="small" />
                    </button>
                  </div>

                  <span className="oiInv__lineTotal">
                    {(row.product?.price * row.quantity).toFixed(2)} <small>{user?.companyCurrencySymbol ?? ''}</small>
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="oiInv__totalBar">
          <strong>{i18n.t('generic.total')}</strong>
          <span>
            {totalPrice.toFixed(2)} {user?.companyCurrencySymbol ?? ''}
          </span>
        </div>
      </div>

      {showOldInvoice && invoice && invoice.invoiceRows.length > 0 && (
        <div className="oiInv__card oiInv__cardHistory">
          <div className="oiInv__header oiInv__headerHistory">
            <Receipt className="header-icon muted" />
            <h3 className="oiInv__title">{i18n.t('tables.oldInvoiceTitle')}</h3>
            <span className="oiInv__invoice-status"> {i18n.t('tables.invoiceStatusClosed')} </span>
          </div>
          <div className="oiInv__list oiInv__listHistory">
            {invoice.invoiceRows.map((row, index) => (
              <div key={index} className="oiInv__row">
                <div className="oiInv__meta">
                  <div className="oiInv__code">{row.product?.code}</div>
                  <div className="oiInv__sub">
                    <span className="oiInv__name">{row?.product?.name}</span>
                    <span className="oiInv__linePrice">
                      {row.quantity} x {getOldInvoiceUnitPrice(row).toFixed(2)} {user?.companyCurrencySymbol ?? ''}
                    </span>

                    {row.note && (
                      <div className="oiInv__history-note" style={{ fontSize: '11px', color: '#2e7d32', fontStyle: 'italic', marginTop: '2px' }}>
                        * {row.note}
                      </div>
                    )}
                  </div>
                </div>
                <div className="oiInv__lineTotal">
                  {getOldInvoiceLineTotal(row).toFixed(2)} {user?.companyCurrencySymbol ?? ''}
                </div>
              </div>
            ))}
          </div>
          <div className="oiInv__totalBar">
            <strong>{i18n.t('tables.totalOldInvoice')}</strong>
            <span>
              {oldInvoiceTotal.toFixed(2)} {user?.companyCurrencySymbol ?? ''}
            </span>
          </div>
        </div>
      )}

      {showCloseConfirm && (
        <GeneralModal
          headerName={i18n.t('generic.confirmation')}
          handleClose={() => setShowCloseConfirm(false)}
          handleClick={confirmCloseInvoice}
          variant="warning"
          width="680px"
          height="320px"
          footerActionsSpread
        >
          <div className="invoiceCloseConfirm">
            <div className="invoiceCloseConfirm__selectRow">
              <select className="gmFooterContent invoiceCloseConfirm__select" value={selectedSubjectId} onChange={(e) => onSubjectChange?.(e.target.value)}>
                <option value="">{i18n.t('invoice.selectSubject', { defaultValue: 'Select subject' })}</option>
                {subjects.map((subject) => (
                  <option key={subject.id} value={subject.id}>
                    {subject.name} - {subject.nipt}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="invoiceCloseConfirm__clearBtn"
                onClick={() => onSubjectChange?.('')}
                disabled={!selectedSubjectId}
                aria-label={i18n.t('generic.clear', { defaultValue: 'Clear' })}
                title={i18n.t('generic.clear', { defaultValue: 'Clear' })}
              >
                x
              </button>
            </div>
            <p className="invoiceCloseConfirm__text">{i18n.t('tables.confirmCloseInvoice')}</p>
          </div>
        </GeneralModal>
      )}
    </div>
  );
};

export default InvoiceSection;
